import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, track } from "@/lib/api";
import { toast } from "sonner";

const eur = v => v == null ? null : `€${Math.round(v).toLocaleString("it-IT")}`;
const pct = v => v == null ? null : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;

const LOWER_BETTER = new Set(["DSO", "PERSONNEL_TO_VALUE_ADDED"]);
const KPI_NAMES = { EBITDA_MARGIN: "Margine EBITDA", ROE: "ROE", CURRENT_RATIO: "Liquidità corrente",
  DSO: "Giorni di incasso", PERSONNEL_TO_VALUE_ADDED: "Costo personale/valore aggiunto",
  EBIT_MARGIN: "Margine EBIT", ASSET_TURNOVER: "Rotazione attivo" };

function esito(p) {
  if (p.actual == null || !p.benchmark_available) return null;
  const lower = LOWER_BETTER.has(p.kpi_code);
  const good = lower ? p.actual <= p.median : p.actual >= p.median;
  const vs = p.actual >= p.median ? "sopra la mediana" : "sotto la mediana";
  return { good, label: vs };
}

export default function Report() {
  const [summary, setSummary] = useState(null);
  const [realScore, setRealScore] = useState(null);
  const [opp, setOpp] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionsLocked, setActionsLocked] = useState(false);
  const [goalsData, setGoalsData] = useState({ goals: [], summary: null });
  const [goalProg, setGoalProg] = useState({});
  const [simAction, setSimAction] = useState(null);
  const [scSpec, setScSpec] = useState([]);
  const [scParams, setScParams] = useState({});
  const [scResult, setScResult] = useState(null);
  const [fbSent, setFbSent] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/report/summary").then(r => setSummary(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/score/latest").then(r => setRealScore(r.data)).catch(() => {});
    api.get("/opportunities/result").then(r => { setOpp(r.data); if (r.data?.status === "OK") track("opportunity_viewed", { pnl_available: r.data.headline?.BASE?.annual_pnl_opportunity != null, cash_available: r.data.headline?.BASE?.one_off_cash_release != null }); }).catch(() => {});
    track("report_viewed");
    api.post("/industry/calculate", {})
      .then(r => { if (r.data?.status === "OK" || r.data?.idempotent_reuse) return api.post("/actions/generate", {}); })
      .catch(() => {})
      .finally(() => {
        api.get("/industry/result").then(r => setIndustry(r.data)).catch(() => {});
        api.get("/actions").then(r => { setActions(r.data?.actions || []); setActionsLocked(!!r.data?.locked); }).catch(() => {});
      });
    api.get("/goals").then(r => setGoalsData(r.data || { goals: [] })).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    const h = window.location.hash;
    if (h) setTimeout(() => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" }), 200);
  }, [loading]);

  const goalStatusUI = { ON_TRACK: ["In linea con l'obiettivo", "#10B981"], AT_RISK: ["Obiettivo a rischio", "#FF6A24"],
    ACHIEVED: ["Obiettivo raggiunto", "#5424D5"], MISSED: ["Obiettivo non raggiunto entro la scadenza", "#DC2626"],
    NO_UPDATE: ["Aggiornamento dati necessario", "#7A7788"], PAUSED: ["Obiettivo in pausa", "#7A7788"],
    CANCELLED: ["Obiettivo annullato", "#7A7788"], ACTIVE: ["Attivo", "#7A7788"] };

  const openSim = async (a) => {
    setSimAction(a); setScResult(null); setScParams({});
    try { const r = await api.get(`/scenarios/params/${a.scenario_template}`); setScSpec(r.data.params || []); }
    catch { toast.error("Scenario non disponibile"); }
  };
  const runSim = async () => {
    try {
      const r = await api.post("/scenarios/calculate", { scenario_type: simAction.scenario_template, parameters: scParams, action_id: simAction.action_id });
      if (r.data.status === "OK") track("scenario_run", { scenario_type: simAction.scenario_template });
      setScResult(r.data);
      if (r.data.status !== "OK") toast.error("Parametri mancanti o non validi");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveGoal = async () => {
    try {
      await api.post("/goals", { action_id: simAction.action_id, scenario_result_id: scResult.scenario_result_id,
        tracking_metric: simAction.tracking_metric, metric_code: simAction.tracking_metric,
        baseline_value: simAction.current_value, target_value: simAction.target_value, target_date: new Date(Date.now() + (simAction.horizon_days || 90) * 864e5).toISOString().slice(0, 10) });
      track("goal_created", { metric_code: simAction.tracking_metric });
      toast.success("Obiettivo salvato — baseline registrata");
      setSimAction(null); setScResult(null);
      api.get("/goals").then(r => setGoalsData(r.data || { goals: [] })).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refreshGoal = async (g) => {
    try {
      await api.post(`/goals/${g.goal_id}/refresh-progress`, {});
      const r = await api.get(`/goals/${g.goal_id}/progress`);
      setGoalProg(prev => ({ ...prev, [g.goal_id]: r.data }));
      api.get("/goals").then(r2 => setGoalsData(r2.data || { goals: [] })).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendFeedback = async (useful) => {
    try { await api.post("/feedback", { context: "report", useful }); setFbSent(true); } catch { /* non-blocking */ }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Caricamento report…</div>;
  if (!summary || summary.detail) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="text-3xl font-extrabold">Nessun report ancora</h1>
      <p className="text-[#524F5E] mt-3">Completa un Business Check per generare il tuo report.</p>
      <Link to="/business-check" className="ic-btn-primary inline-block mt-6" data-testid="report-cta-check">Fai il Business Check</Link>
    </div>
  );

  const s = summary.snapshot || {};
  const score = summary.score;
  const oppOk = opp?.status === "OK";
  const oppBase = opp?.headline?.BASE || {};
  const oppPnl = oppOk ? oppBase.annual_pnl_opportunity : null;
  const oppCash = oppOk ? oppBase.one_off_cash_release : null;
  const oppMsg = !opp ? "Stima non disponibile con sufficiente affidabilità."
    : opp?.status === "LOCKED" ? (opp.user_message || "Disponibile dal piano GROW.")
    : opp?.headline?.BASE?.pnl_status === "NO_OPPORTUNITY" && oppCash == null ? "Nessuna opportunità rispetto alla mediana."
    : "Dati insufficienti per una stima attendibile.";

  const snapshotRows = [
    ["revenue", "Fatturato"], ["net_income", "Utile netto"], ["net_margin", "Margine netto", true],
    ["ebitda", "EBITDA"], ["ebitda_margin", "Margine EBITDA", true], ["gross_profit", "Margine lordo"],
    ["gross_margin", "Margine lordo %", true], ["cogs", "COGS"], ["personnel", "Costo del personale"],
    ["cash", "Liquidità"], ["financial_debt", "Debiti finanziari"], ["inventory", "Magazzino"],
  ].filter(([k]) => s[k] != null);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="engine-report">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Report aziendale</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Come sta la tua impresa</h1>
        </div>
        <Link to="/business-check" className="ic-btn-ghost text-sm">Ricalcola</Link>
      </div>

      {/* 1. Executive summary */}
      <div className="ic-card p-6 mb-10 border-[#5424D5]/30" data-testid="exec-summary">
        <div className="flex flex-wrap items-center gap-6">
          {score?.overall != null ? (
            <>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#7A7788]">ImpresaChiara Score</p>
                <p className="text-5xl font-extrabold text-[#5424D5]">{score.overall}<span className="text-lg text-[#7A7788]">/100</span></p>
                <p className="font-bold mt-1">{score.band}</p>
              </div>
              <p className="text-sm text-[#524F5E] flex-1 min-w-[240px]">
                {summary.score_drivers?.length > 0
                  ? <>Cosa influenza il punteggio: {summary.score_drivers.slice(0, 3).map(d => `${KPI_NAMES[d.kpi_code] || d.kpi_code} (${d.position || `score ${d.score}`})`).join(", ")}.</>
                  : "Copertura KPI insufficiente per spiegare il punteggio."}
              </p>
            </>
          ) : (
            <p className="text-sm text-[#524F5E]">Dati insufficienti per calcolare uno Score affidabile: servono più KPI finanziari con benchmark di settore.</p>
          )}
        </div>
        <p className="text-[10px] text-[#7A7788] mt-4">Fonte peer: BACH 2024 (ECCBSO), piccole imprese italiane · confidence benchmark ≥70 · Le analisi sono stime informative e non sostituiscono la consulenza professionale.</p>
      </div>

      {/* 2. I tuoi numeri */}
      <h2 className="text-xl font-bold mb-4">I tuoi numeri</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10" data-testid="snapshot">
        {snapshotRows.map(([k, label, isPct]) => (
          <div key={k} className={`ic-card p-5 ${k === "net_income" ? "border-[#10B981]/50 md:col-span-1 col-span-2" : ""}`} data-testid={`snap-${k}`}>
            <p className="text-xs uppercase tracking-widest text-[#7A7788]">{label}</p>
            <p className={`font-extrabold mt-2 ${k === "net_income" ? "text-3xl text-[#10B981]" : "text-2xl"}`}>
              {isPct ? pct(s[k]) : eur(s[k])}
            </p>
            {k === "cogs" && s.cogs_status && s.cogs_status !== "EXACT" && (
              <p className="text-[10px] text-[#7A7788] mt-1">Metodo: {s.cogs_status === "SECTOR_PROXY" ? "stima di settore" : s.cogs_status.toLowerCase()}</p>
            )}
          </div>
        ))}
      </div>

      {/* 4. Confronto con imprese simili */}
      <h2 className="text-xl font-bold mb-1">Confronto con imprese simili</h2>
      <p className="text-xs text-[#7A7788] mb-4">Piccole imprese italiane del tuo settore · Fonte: BACH 2024 (ECCBSO)</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="peer-comparison">
        {summary.peers?.filter(p => p.actual != null || p.benchmark_available).map(p => {
          const e = esito(p);
          return (
            <div key={p.kpi_code} className="ic-card p-5" data-testid={`peer-${p.kpi_code}`}>
              <p className="text-xs uppercase tracking-widest text-[#7A7788]">{p.label}</p>
              <div className="flex items-baseline gap-2 mt-2">
                <p className="text-2xl font-extrabold">{p.unit === "%" ? pct(p.actual) : p.actual ?? "—"}{p.unit === "gg" ? " gg" : p.unit === "x" ? "x" : ""}</p>
                {e && <span className="text-xs font-bold" style={{ color: e.good ? "#10B981" : "#FF6A24" }}>{e.label}</span>}
              </div>
              {p.benchmark_available ? (
                <div className="text-xs text-[#524F5E] mt-2 space-y-0.5">
                  <p>Mediana: <b>{p.unit === "%" ? pct(p.median) : p.median}</b> · Top 25%: <b>{p.unit === "%" ? pct(p.p75) : p.p75}</b></p>
                  <p className="text-[10px] text-[#7A7788]">Fonte: {p.source} · affidabilità {p.confidence}/100{p.fallback_used ? " · peer allargato alla sezione" : ""}</p>
                </div>
              ) : (
                <p className="text-[10px] text-[#7A7788] mt-2">Benchmark di settore non disponibile per questo KPI.</p>
              )}
            </div>
          );
        })}
      </div>

      {/* 5/6. Punti di forza + Criticità */}
      {(summary.strengths?.length > 0 || summary.criticalities?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          {summary.strengths?.length > 0 && (
            <div className="ic-card p-6 border-[#10B981]/30" data-testid="strengths">
              <h3 className="font-bold text-[#10B981] mb-3">Punti di forza</h3>
              {summary.strengths.map((x, i) => (
                <p key={i} className="text-sm mb-2"><b>{KPI_NAMES[x.kpi_code] || x.kpi_code}</b> — {x.position}{x.level === "forte" ? " (sopra il 75% delle imprese simili)" : ""}</p>
              ))}
            </div>
          )}
          {summary.criticalities?.length > 0 && (
            <div className="ic-card p-6 border-[#FF6A24]/40" data-testid="criticalities">
              <h3 className="font-bold text-[#FF6A24] mb-3">Criticità</h3>
              {summary.criticalities.map((x, i) => (
                <p key={i} className="text-sm mb-2"><b>{KPI_NAMES[x.kpi_code] || x.kpi_code}</b> — {x.position || "sotto il riferimento di settore"}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 7. Opportunità economiche (Opportunity Engine v1) */}
      {oppOk ? (
        <>
          <h2 className="text-xl font-bold mb-4" id="opportunita">Le maggiori opportunità economiche <span className="text-xs font-normal text-[#7A7788]">benchmark BACH 2024 · Opportunity Engine v{opp.opportunity_engine_version}</span></h2>
          <div className="grid md:grid-cols-2 gap-5 mb-3">
            <div className="ic-card p-6 border-[#5424D5]/30" data-testid="opp-pnl-card">
              <p className="text-xs uppercase tracking-widest text-[#7A7788]">{opp.copy.pnl_headline}</p>
              {oppPnl != null ? <p className="text-4xl font-extrabold my-2 text-[#5424D5]">{eur(oppPnl)}</p>
                : <p className="text-sm text-[#7A7788] my-2">Nessuna leva economica identificata sulla baseline attuale.</p>}
              <p className="text-xs text-[#7A7788]">{opp.copy.base_scenario}</p>
              {opp.headline?.AMBITIOUS?.annual_pnl_opportunity != null && (
                <p className="text-xs text-[#524F5E] mt-2">{opp.copy.ambitious_scenario}: <b>{eur(opp.headline.AMBITIOUS.annual_pnl_opportunity)}</b></p>
              )}
              {oppBase.driver_personnel_eur != null && (
                <p className="text-[10px] text-[#7A7788] mt-2">Una parte del gap potrebbe essere collegata all'efficienza del personale (driver, mai sommato al totale).</p>
              )}
            </div>
            <div className="ic-card p-6 border-[#10B981]/30" data-testid="opp-cash-card">
              <p className="text-xs uppercase tracking-widest text-[#7A7788]">{opp.copy.cash_headline}</p>
              {oppCash != null ? <p className="text-4xl font-extrabold my-2 text-[#10B981]">{eur(oppCash)}</p>
                : <p className="text-sm text-[#7A7788] my-2">Nessuna liquidità liberabile identificata sulla baseline attuale.</p>}
              <p className="text-xs text-[#7A7788]">{opp.copy.base_scenario} · liquidità una tantum, non profitto annuo</p>
              {opp.headline?.AMBITIOUS?.one_off_cash_release != null && (
                <p className="text-xs text-[#524F5E] mt-2">{opp.copy.ambitious_scenario}: <b>{eur(opp.headline.AMBITIOUS.one_off_cash_release)}</b></p>
              )}
            </div>
          </div>
          <p className="text-[10px] text-[#7A7788] mb-2" data-testid="opp-disclaimer">{opp.disclaimer} Non sostituisce la consulenza professionale.</p>
        </>
      ) : (
        <div className="ic-card p-6 mb-10" data-testid="opp-unavailable">
          <p className="font-semibold">Le maggiori opportunità economiche</p>
          <p className="text-sm text-[#7A7788] mt-1">{oppMsg}</p>
          {opp?.status === "LOCKED" && <Link to="/pricing" className="ic-btn-primary text-sm inline-block mt-3">Vedi i piani</Link>}
        </div>
      )}

      {/* 8. CFO Simulator CTA */}
      <div className="ic-card p-6 mb-10 border-[#5424D5]/30 flex flex-wrap items-center justify-between gap-4" data-testid="cfo-cta">
        <div>
          <h2 className="text-xl font-bold">CFO Simulator</h2>
          <p className="text-sm text-[#524F5E] mt-1">Parti dai tuoi numeri reali e simula più leve insieme: prezzi, costi, personale, incassi. Vedi l'impatto su EBITDA, utile e liquidità.</p>
        </div>
        <Link to="/cfo-simulator" className="ic-btn-primary" data-testid="cfo-open">Apri il CFO Simulator</Link>
      </div>

      {/* 9. Analisi specifica settore */}
      {industry?.status === "OK" && (
        <div className="mb-10" data-testid="industry-section">
          <h2 className="text-xl font-bold mb-1">Analisi specifica del settore <span className="text-xs font-normal text-[#7A7788]">{industry.vertical?.label}</span></h2>
          {industry.disabled_rules?.length > 0 && (
            <p className="text-[10px] text-[#7A7788] mb-3">Alcuni benchmark operativi di settore sono in fase di validazione: i valori aziendali sono reali, il confronto peer sarà attivato appena la fonte è approvata.</p>
          )}
          {(() => {
            const calc = (industry.operational_kpis || []).filter(k => k.calculation_status === "OK" && k.value != null);
            return calc.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {calc.slice(0, 4).map(k => (
                  <div key={k.kpi_code} className="ic-card p-4" data-testid={`opkpi-${k.kpi_code}`}>
                    <p className="text-[10px] uppercase tracking-widest text-[#7A7788]">{k.kpi_name}</p>
                    <p className="text-xl font-extrabold mt-1">{Number(k.value).toLocaleString("it-IT", { maximumFractionDigits: 1 })}{k.kpi_code.endsWith("_PCT") ? "%" : ""}</p>
                    {k.benchmark_status === "SOURCE_REQUIRED" && <p className="text-[9px] text-[#7A7788]">Benchmark operativo in fase di validazione</p>}
                  </div>
                ))}
              </div>
            ) : null;
          })()}
          {actions.length === 0 ? (
            <div className="ic-card p-6 text-sm text-[#7A7788]" data-testid="no-active-diagnosis">{actionsLocked ? "Le azioni consigliate sono disponibili dal piano GROW." : "Nessuna criticità operativa attiva rilevata con i dati disponibili."}</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {actions.map(a => (
                <div key={a.action_id} className="ic-card p-6" data-testid={`action-${a.action_id}`}>
                  <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">{a.diagnosis_text || a.diagnosis_code}</p>
                  <h4 className="font-bold mt-2">{a.title}</h4>
                  <p className="text-sm text-[#524F5E] mt-1">{a.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-[#7A7788]">
                    <span>Priorità <b className="text-[#0E0D13]">{a.priority_score}/100</b></span>
                    <span>Entro <b className="text-[#0E0D13]">{a.horizon_days} gg</b></span>
                    {a.target_value != null && <span>Obiettivo {a.target_variable}: <b className="text-[#0E0D13]">{a.target_value}</b></span>}
                  </div>
                  {a.expected_impact_eur != null ? (
                    <p className="text-sm font-bold text-[#5424D5] mt-2">Impatto potenziale stimato: {eur(a.expected_impact_eur)}</p>
                  ) : (
                    <p className="text-[10px] text-[#7A7788] mt-2">Impatto economico calcolabile con la simulazione.</p>
                  )}
                  <button className="ic-btn-primary text-sm mt-4" data-testid={`simula-${a.action_id}`} onClick={() => openSim(a)}>Simula</button>
                  {simAction?.action_id === a.action_id && (
                    <div className="mt-4 border-t border-[#E5E4EB] pt-4" data-testid="scenario-panel">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#7A7788] mb-3">Simulazione · {a.scenario_template}</p>
                      {scSpec.map(p => (
                        <label key={p.key} className="block text-xs mb-2">{p.label}
                          <input type="number" step="any" className="ic-input mt-1 w-full" data-testid={`sc-param-${p.key}`}
                            value={scParams[p.key] ?? ""} onChange={e => setScParams({ ...scParams, [p.key]: e.target.value === "" ? undefined : Number(e.target.value) })} />
                        </label>
                      ))}
                      <button className="ic-btn-ghost text-sm mt-2" data-testid="run-scenario" onClick={runSim}>Calcola scenario</button>
                      {scResult?.status === "OK" && (
                        <div className="mt-3 bg-[#F4F4F7] rounded-xl p-4" data-testid="scenario-result">
                          <p className="text-xs text-[#7A7788]">{scResult.output_label || "Risultato simulazione"}</p>
                          {Object.entries(scResult.primary_output || {}).map(([k, v]) => (
                            <p key={k} className="text-2xl font-extrabold text-[#5424D5]" data-testid={`sc-out-${k}`}>{eur(v)}</p>
                          ))}
                          {Object.entries(scResult.secondary_outputs || {}).filter(([, v]) => typeof v === "number").map(([k, v]) => (
                            <p key={k} className="text-xs text-[#524F5E] mt-1">{k.replace(/_/g, " ")}: <b>{Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</b></p>
                          ))}
                          <p className="text-[10px] text-[#7A7788] mt-2">{scResult.disclaimer}</p>
                          <button className="ic-btn-primary text-sm mt-3" data-testid="save-goal" onClick={saveGoal}>Salva obiettivo</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 10. Obiettivi */}
      {goalsData.goals.length > 0 && (
        <div className="mb-10" data-testid="goals-section">
          <h2 className="text-xl font-bold mb-1" id="obiettivi">I tuoi obiettivi</h2>
          {goalsData.summary && (
            <p className="text-xs text-[#7A7788] mb-4" data-testid="goals-summary">
              {goalsData.summary.active} attivi · {goalsData.summary.on_track} in linea · {goalsData.summary.at_risk} a rischio · {goalsData.summary.achieved} raggiunti
            </p>
          )}
          <div className="grid md:grid-cols-2 gap-5">
            {goalsData.goals.map(g => {
              const st = goalStatusUI[g.status] || goalStatusUI.ACTIVE;
              const prog = goalProg[g.goal_id]?.progress;
              const impact = goalProg[g.goal_id]?.realized_impact;
              return (
                <div key={g.goal_id} className="ic-card p-6" data-testid={`goal-${g.goal_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold">Obiettivo: {(g.metric_code || g.tracking_metric || "KPI").replace(/_/g, " ")} {g.target_value} entro {String(g.target_date || "").slice(0, 10).split("-").reverse().slice(0, 2).join("/")}</p>
                      <p className="text-xs text-[#7A7788] mt-1">Baseline: {g.baseline_value} (immutabile) · revisione v{g.goal_revision || 1}</p>
                    </div>
                    <span className="ic-badge" style={{ color: st[1] }} data-testid={`goal-status-${g.goal_id}`}>{st[0]}</span>
                  </div>
                  {prog ? (
                    <div className="mt-4">
                      <div className="h-2 bg-[#F4F4F7] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#5424D5] to-[#FF6A24]" style={{ width: `${(prog.display_progress || 0) * 100}%` }} />
                      </div>
                      <p className="text-sm mt-2" data-testid={`goal-progress-${g.goal_id}`}>{Math.round((prog.display_progress || 0) * 100)}% del percorso completato · attuale: <b>{prog.current_value}</b></p>
                      <p className="text-xs mt-1" style={{ color: (goalStatusUI[prog.status] || [])[1] }}>{prog.status_copy}</p>
                      <div className="flex gap-6 mt-3 text-xs">
                        {prog.estimated_impact_eur != null && <span className="text-[#7A7788]">Stima iniziale: <b className="text-[#0E0D13]">{eur(prog.estimated_impact_eur)}</b></span>}
                        {impact?.realized_impact_eur != null && <span className="text-[#7A7788]">Impatto osservato stimato: <b className="text-[#10B981]">{eur(impact.realized_impact_eur)}</b> <i>({impact.user_label})</i></span>}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-[#7A7788] mt-3">Aggiorna i dati per misurare il progresso — l'obiettivo resta salvato.</p>
                  )}
                  <button className="ic-btn-ghost text-sm mt-4" data-testid={`goal-refresh-${g.goal_id}`} onClick={() => refreshGoal(g)}>Vedi progresso</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feedback beta */}
      <div className="flex items-center gap-3 mb-4" data-testid="feedback-widget">
        {!fbSent ? (
          <>
            <span className="text-xs text-[#7A7788]">Questa analisi ti è stata utile?</span>
            <button className="ic-btn-ghost text-xs px-3 py-1" data-testid="feedback-yes" onClick={() => sendFeedback(true)}>Utile</button>
            <button className="ic-btn-ghost text-xs px-3 py-1" data-testid="feedback-no" onClick={() => sendFeedback(false)}>Non utile</button>
          </>
        ) : <span className="text-xs text-[#10B981] font-semibold">Grazie per il feedback.</span>}
      </div>
    </div>
  );
}
